import React from "react";
import {
  Box,
  Typography,
  Chip,
  LinearProgress,
} from "@mui/material";
import {
  Book as BookIcon,
  CheckCircle as CheckCircleIcon,
} from "@mui/icons-material";
import { useFormattedContent } from "@/hooks";
import useVocabularyPagination from "./vocabulary/useVocabularyPagination";
import WordCard from "./vocabulary/WordCard";
import VocabularyQuestionPanel from "./vocabulary/VocabularyQuestionPanel";

const VocabularySection = ({
  selectedDay,
  progress,
  exerciseId,
}) => {
  const {
    currentWord,
    currentSlide,
    totalWords,
    isLoading,
    canGoNext,
    canGoPrev,
    handleNextWord,
    handlePrevWord,
  } = useVocabularyPagination({ exerciseId, selectedDay });

  // Hooks must be called unconditionally (before early returns)
  const { hasContent: hasExplanation } = useFormattedContent(currentWord?.explanation);
  const { hasContent: hasExample } = useFormattedContent(currentWord?.example);

  const isCompleted = progress?.partProgress?.vocabulary;

  // ─── Loading state ───
  if (isLoading) {
    return (
      <Box textAlign="center" py={4}>
        <LinearProgress />
        <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
          Loading vocabulary...
        </Typography>
      </Box>
    );
  }

  // ─── Empty state ───
  if (!currentWord && !isLoading) {
    return (
      <Box textAlign="center" py={4}>
        <BookIcon sx={{ fontSize: 60, color: "text.secondary", mb: 2 }} />
        <Typography variant="h6" color="text.secondary" gutterBottom>
          No vocabulary words available
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Vocabulary words for this day will appear here.
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      {/* Section Header */}
      <Box
        sx={{
          background: "linear-gradient(135deg, #2196F3 0%, #1976D2 100%)",
          borderRadius: 2,
          p: 3,
          mb: 3,
          color: "white",
        }}
      >
        <Typography variant="h4" fontWeight="bold" gutterBottom>
          Vocabulary: Day {selectedDay.dayNumber}
        </Typography>
        <Box display="flex" alignItems="center" gap={2} mt={2}>
          <Chip
            icon={<BookIcon />}
            label={`DAY ${selectedDay.dayNumber}`}
            color="warning"
            sx={{ fontWeight: "bold" }}
          />
          <Chip
            label={selectedDay.title}
            variant="outlined"
            sx={{ color: "white", borderColor: "white" }}
          />
          {isCompleted && (
            <Chip
              icon={<CheckCircleIcon />}
              label="Completed"
              color="success"
            />
          )}
        </Box>
      </Box>

      {/* Progress Indicator */}
      <Box sx={{ mb: 3 }}>
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          mb={1}
        >
          <Typography variant="body2" color="text.secondary">
            Word {currentSlide + 1} of {totalWords}
          </Typography>
        </Box>
        <LinearProgress
          variant="determinate"
          value={((currentSlide + 1) / totalWords) * 100}
          sx={{ height: 8, borderRadius: 4 }}
        />
      </Box>

      {/* Word Flashcard */}
      <WordCard
        currentWord={currentWord}
        hasExplanation={hasExplanation}
        hasExample={hasExample}
        canGoNext={canGoNext}
        canGoPrev={canGoPrev}
        onNext={handleNextWord}
        onPrev={handlePrevWord}
      />

      {/* Practice Questions */}
      <VocabularyQuestionPanel
        exerciseId={exerciseId}
        selectedDay={selectedDay}
      />
    </Box>
  );
};

export default VocabularySection;
